// Die Zeichnung auf einem Foto als Bild (Plan E2, „Ich entscheide“): Am
// Gerät und am Server ist die Zeichnung Daten – Striche aus Punkten
// zwischen 0 und 1 (aufnahme-vertrag.ts). Das Bild dazu entsteht hier, an
// EINER Stelle: die Ebene über dem Foto in der Doku-App, später im Büro und
// im PDF (E3). So sieht sie überall gleich aus, und kein SVG vom Gerät
// kommt je ins Büro.
//
// Sicher, weil nichts vom Gerät als Text ins SVG gelangt: Hinein kommen nur
// Zahlen, die hier gerechnet und als Zahl geschrieben werden, und feste
// Texte aus diesem Code. Das Werkzeug entscheidet nur, welche Form
// entsteht; ein unbekanntes ergibt keine. `zeichnungAlsSvg` prüft die
// Zeichnung vorher zusätzlich gegen den Vertrag (Grenzen, Wertebereich).
//
// Die Formen – wie im Mockup, in Lime:
//   Stift  freie Linie durch alle Punkte (ein einzelner Punkt: ein Tupfen)
//   Kreis  aufgezogen: die zwei Punkte sind die Ecken des Rahmens
//   Pfeil  von – bis, mit Spitze am Ende
// Unter jeder Form liegt ein dunkler Rand, damit Lime auch auf heller Wand
// zu sehen ist.
//
// Seit 22.10.2026 (O66, Probe): Jeder Strich hat seine Stärke – dünn,
// mittel, dick. Die Breite, die Pfeilspitze und der dunkle Rand richten sich
// nach ihr; ohne Angabe ist ein Strich „mittel“.
//
// Seit E3b (O66, Gernot 22.10.2026): drei Stärken – „dünn“ ganz dezent
// (rund halb so breit wie „mittel“) und am Gerät vorgewählt, „mittel“ wie
// seit dem 22.10., „dick“ wie davor – und drei Farben: Lime (die Marke),
// Türkis (die zweite Markenfarbe) und Pink. Auf roten Ziegeln tragen Lime
// und Türkis, am Himmel Lime und Pink, auf weißer Wand Pink und Türkis; alle
// mit dem dunklen Rand. Ohne Angabe: Lime. Die Farbe wird hier aus einer
// festen Liste genommen – nie als Text vom Gerät.

import { zeichnung as zeichnungVertrag, type StrichFarbe, type StrichStaerke, type Zeichnung } from "./aufnahme-vertrag.ts";

/** Lime – die Farbe jeder Zeichnung ohne Angabe (vor E3b alle). */
export const ZEICHEN_FARBE = "#D4F000";
/** Die Farben als Werte fürs Bild – feste Texte aus diesem Code. */
export const STRICH_FARBWERTE: Readonly<Record<StrichFarbe, string>> = { lime: ZEICHEN_FARBE, tuerkis: "#00BFAF", pink: "#FF3D9A" };
/** Die Farbe, wenn ein Strich keine (bekannte) hat. */
export const FARBE_VORGABE: StrichFarbe = "lime";
/** Der dunkle Rand unter jeder Form: Farbe, Deckkraft und wie viel breiter als der Strich. */
export const ZEICHEN_RAND = { farbe: "#000000", deckkraft: 0.35, faktor: 1.6 } as const;
/**
 * Die Strichbreite je Stärke als Anteil der kürzeren Bildseite – so ist ein
 * Strich auf jedem Foto gleich kräftig. „dick“ ist die Breite bis 22.10.2026
 * (bei 2048 × 1536 Pixeln 32 Pixel, am iPhone angezeigt rund 6 Pixel wie im
 * Mockup); Manuel fand Pfeil und Kreis am iPad damit zu fett (O66). Deshalb
 * ist „mittel“ die Vorgabe – auch für Striche ohne Angabe, ältere
 * Zeichnungen werden dadurch dünner (so gewollt).
 */
export const STRICH_ANTEILE: Readonly<Record<StrichStaerke, number>> = { duenn: 1 / 160, mittel: 1 / 72, dick: 1 / 48 };
/** Die Stärke, wenn ein Strich keine (bekannte) hat. */
export const STRICH_VORGABE: StrichStaerke = "mittel";
/** Die Spitze des Pfeils: Länge in Strichbreiten (höchstens die halbe Pfeillänge) und Winkel der Flügel. */
const SPITZE_STRICHE = 4;
const SPITZE_WINKEL = 0.5;

/** Die Grenzen, in denen ein Bild gezeichnet wird – so groß wird kein Foto. */
const GROESSTE_SEITE = 20_000;

/** Eine Form in Pixeln des Bildes, mit der Breite und der Farbe ihres Strichs. */
export type ZeichenForm = { breite: number; farbe: StrichFarbe } & (
  | { art: "linie"; x1: number; y1: number; x2: number; y2: number }
  | { art: "polylinie"; punkte: [number, number][] }
  | { art: "ellipse"; cx: number; cy: number; rx: number; ry: number }
);

/** Die Stärke eines Strichs – fehlt sie oder ist sie keine bekannte (ungeprüfte Daten), ist er „mittel“. */
export function strichStaerke(s: { staerke?: unknown }): StrichStaerke {
  const st = s.staerke;
  return typeof st === "string" && Object.hasOwn(STRICH_ANTEILE, st) ? (st as StrichStaerke) : STRICH_VORGABE;
}

/** Die Farbe eines Strichs – fehlt sie oder ist sie keine bekannte (ungeprüfte Daten), ist er Lime. */
export function strichFarbe(s: { farbe?: unknown }): StrichFarbe {
  const f = s.farbe;
  return typeof f === "string" && Object.hasOwn(STRICH_FARBWERTE, f) ? (f as StrichFarbe) : FARBE_VORGABE;
}

/** Die Breite eines Strichs dieser Stärke in Pixeln eines Bildes dieser Größe – mindestens 1. */
export function strichBreite(breite: number, hoehe: number, staerke: StrichStaerke = STRICH_VORGABE): number {
  return Math.max(1, Math.min(breite, hoehe) * STRICH_ANTEILE[strichStaerke({ staerke })]);
}

/** Eine Zahl, wie sie ins SVG kommt: auf eine Stelle gerundet; was keine Zahl ist, wird 0. */
function zahl(wert: number): string {
  const n = Number(wert);
  return Number.isFinite(n) ? String(Math.round(n * 10) / 10) : "0";
}

/** Ein Punkt (0 bis 1) in Pixeln des Bildes. */
function inPixel(p: readonly [number, number] | undefined, breite: number, hoehe: number): [number, number] {
  return [Number(p?.[0] ?? 0) * breite, Number(p?.[1] ?? 0) * hoehe];
}

/**
 * Die Formen einer Zeichnung in Pixeln eines Bildes dieser Größe. Prüft
 * selbst nichts – was nicht passt, ergibt keine oder eine leere Form.
 */
export function zeichnungFormen(z: Zeichnung, breite: number, hoehe: number): ZeichenForm[] {
  const formen: ZeichenForm[] = [];
  for (const s of z.striche) {
    const strich = strichBreite(breite, hoehe, strichStaerke(s));
    const farbe = strichFarbe(s);
    const punkte = s.punkte.map((p) => inPixel(p, breite, hoehe));
    const [a, b] = [punkte[0], punkte[punkte.length - 1]];
    if (!a || !b) continue;
    switch (s.werkzeug) {
      case "stift":
        // Ein einzelner Punkt: eine Linie ohne Länge – mit runden Enden ein Tupfen.
        formen.push(
          punkte.length === 1
            ? { art: "linie", x1: a[0], y1: a[1], x2: a[0], y2: a[1], breite: strich, farbe }
            : { art: "polylinie", punkte, breite: strich, farbe },
        );
        break;
      case "kreis":
        formen.push({
          art: "ellipse",
          cx: (a[0] + b[0]) / 2,
          cy: (a[1] + b[1]) / 2,
          rx: Math.abs(b[0] - a[0]) / 2,
          ry: Math.abs(b[1] - a[1]) / 2,
          breite: strich,
          farbe,
        });
        break;
      case "pfeil": {
        formen.push({ art: "linie", x1: a[0], y1: a[1], x2: b[0], y2: b[1], breite: strich, farbe });
        const laenge = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (laenge === 0) break;
        // Die Spitze wächst mit dem Strich: vier Strichbreiten lang.
        const spitze = Math.min(strich * SPITZE_STRICHE, laenge / 2);
        const winkel = Math.atan2(b[1] - a[1], b[0] - a[0]);
        const fluegel = (d: number): [number, number] => [b[0] - spitze * Math.cos(winkel + d), b[1] - spitze * Math.sin(winkel + d)];
        formen.push({ art: "polylinie", punkte: [fluegel(SPITZE_WINKEL), b, fluegel(-SPITZE_WINKEL)], breite: strich, farbe });
        break;
      }
      default:
        // Ein Werkzeug, das es nicht gibt, zeichnet nichts.
        break;
    }
  }
  return formen;
}

/** Eine Form als SVG-Element; `faktor`: wie viel breiter als ihr Strich (der dunkle Rand). */
function formAlsSvg(f: ZeichenForm, faktor: number): string {
  const strich = `stroke-width="${zahl(f.breite * faktor)}"`;
  switch (f.art) {
    case "linie":
      return `<line x1="${zahl(f.x1)}" y1="${zahl(f.y1)}" x2="${zahl(f.x2)}" y2="${zahl(f.y2)}" ${strich}/>`;
    case "polylinie":
      return `<polyline points="${f.punkte.map(([x, y]) => `${zahl(x)},${zahl(y)}`).join(" ")}" ${strich}/>`;
    case "ellipse":
      return `<ellipse cx="${zahl(f.cx)}" cy="${zahl(f.cy)}" rx="${zahl(f.rx)}" ry="${zahl(f.ry)}" ${strich}/>`;
  }
}

/**
 * Der Inhalt der Zeichen-Ebene (ohne das umschließende <svg>) für ein Bild
 * dieser Größe – zum Einbetten in ein <svg viewBox="0 0 breite hoehe">
 * über dem Foto. Erst der dunkle Rand, darüber die Farben; jede Form mit der
 * Breite ihres Strichs. Die Farben je Gruppe: Striche gleicher Farbe
 * hintereinander teilen eine Gruppe, die Reihenfolge der Striche bleibt
 * (ein späterer liegt oben). Eine Zeichnung nur in Lime ergibt genau das
 * Bild von vor E3b.
 */
export function zeichnungSvgInhalt(z: Zeichnung, breite: number, hoehe: number): string {
  const formen = zeichnungFormen(z, breite, hoehe);
  if (formen.length === 0) return "";
  const rand = formen.map((f) => formAlsSvg(f, ZEICHEN_RAND.faktor)).join("");
  const gruppen: { farbe: StrichFarbe; inhalt: string }[] = [];
  for (const f of formen) {
    const letzte = gruppen[gruppen.length - 1];
    if (letzte?.farbe === f.farbe) letzte.inhalt += formAlsSvg(f, 1);
    else gruppen.push({ farbe: f.farbe, inhalt: formAlsSvg(f, 1) });
  }
  return (
    `<g fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<g stroke="${ZEICHEN_RAND.farbe}" stroke-opacity="${ZEICHEN_RAND.deckkraft}">${rand}</g>` +
    gruppen.map((g) => `<g stroke="${STRICH_FARBWERTE[g.farbe]}">${g.inhalt}</g>`).join("") +
    `</g>`
  );
}

/**
 * Die Zeichnung als eigenes SVG in der Größe des Fotos – zum Darüberlegen
 * im Büro und für das PDF (E3). Prüft die Zeichnung vorher gegen den
 * Vertrag und wirft, wenn sie nicht passt.
 */
export function zeichnungAlsSvg(z: unknown, breite: number, hoehe: number): string {
  const geprueft = zeichnungVertrag.parse(z);
  if (
    !Number.isInteger(breite) ||
    !Number.isInteger(hoehe) ||
    breite < 1 ||
    hoehe < 1 ||
    breite > GROESSTE_SEITE ||
    hoehe > GROESSTE_SEITE
  ) {
    throw new RangeError("Breite und Höhe des Fotos müssen ganze Pixel zwischen 1 und 20000 sein.");
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${breite} ${hoehe}" width="${breite}" height="${hoehe}">` +
    zeichnungSvgInhalt(geprueft, breite, hoehe) +
    `</svg>`
  );
}
